import React, { useMemo } from "react";

import * as API from "@ekonsilio/api-livechat";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";

import { RetryIcon } from "../../__components__/icons";

import generateSentAtFromLastMessage from "../../../utils/helpers/generateSentAtFromLastMessage";
import { useWebmessenger } from "../../../utils/webmessenger";

const webmessengerApi = new API.Messaging.Webmessenger();

function MessageStatus({
    _id,
    show,
    type,
    content,
    author,
    status,
    typing,
    onResetIsHover,
}) {
    const intl = useIntl();

    const { conversation, messages, handleSetMessages } = useWebmessenger();

    const isOperator = author?.type === "operator" || author?.type === "agent";

    const latestMessage = useMemo(() => {
        if (!messages?.length) return null;
        return messages[messages.length - 1];
    }, [messages]);

    const messageStatusText = () => {
        if (status.error)
            return intl.formatMessage({
                id: "WEBMESSENGER.MESSAGE_DETAILS.STATUS_ERROR",
                defaultMessage: "Échec de l'envoi",
            });

        if (status.loading)
            return intl.formatMessage({
                id: "WEBMESSENGER.MESSAGE_DETAILS.STATUS_LOADING",
                defaultMessage: "Envoi en cours",
            });

        if (status.readAt)
            return intl.formatMessage({
                id: "WEBMESSENGER.MESSAGE_DETAILS.STATUS_READ",
                defaultMessage: "Lu",
            });

        if (status.receivedAt)
            return intl.formatMessage({
                id: "WEBMESSENGER.MESSAGE_DETAILS.STATUS_DELIVERED",
                defaultMessage: "Distribué",
            });

        return intl.formatMessage({
            id: "WEBMESSENGER.MESSAGE_DETAILS.STATUS_SENT",
            defaultMessage: "Envoyé",
        });
    };

    const { mutate: sendMessage } = useMutation(
        async (message) => await webmessengerApi.sendMessage(message),
        {
            onError: (error, data) => {
                handleSetMessages((prevMessages) => {
                    const messagesCopy = [...prevMessages];

                    const messageIndex = messagesCopy.findIndex(
                        (message) => message._id === data.messageId
                    );

                    if (messageIndex >= 0) {
                        messagesCopy[messageIndex] = {
                            ...messagesCopy[messageIndex],
                            status: {
                                ...messagesCopy[messageIndex].status,
                                error: true,
                                loading: false,
                            },
                        };
                    }

                    return messagesCopy.sort(
                        (a, b) =>
                            new Date(a.createdAt || a.status.sentAt) -
                            new Date(b.createdAt || b.status.sentAt)
                    );
                });
            },
        }
    );

    const handleRetrySendMessage = () => {
        const sentAt = generateSentAtFromLastMessage(latestMessage);

        onResetIsHover();

        handleSetMessages((prevMessages) => {
            const messagesCopy = [...prevMessages];

            const messageIndex = messagesCopy.findIndex(
                (message) => message._id === _id
            );

            if (messageIndex >= 0) {
                messagesCopy[messageIndex] = {
                    ...messagesCopy[messageIndex],
                    status: {
                        ...messagesCopy[messageIndex].status,
                        sentAt,
                        error: false,
                        loading: true,
                    },
                };
            }

            return messagesCopy.sort(
                (a, b) =>
                    new Date(a.createdAt || a.status.sentAt) -
                    new Date(b.createdAt || b.status.sentAt)
            );
        });

        sendMessage({
            type: "text",
            content,
            messageId: _id,
            author: {
                type: "visitor",
            },
            conversation: conversation._id,
            status: {
                sentAt,
            },
        });
    };

    return (
        type !== "system" &&
        show && (
            <div className="ek-chat__message--details">
                {isOperator && author?.name}
                {!status.error &&
                    author?.type === "visitor" &&
                    intl.formatMessage({
                        id: "WEBMESSENGER.MESSAGE_DETAILS.AUTHOR_ME",
                        defaultMessage: "Moi",
                    })}
                {!status.error && !typing && " • "}
                {!typing && DateTime.fromISO(status.sentAt).toFormat("HH'H'mm")}
                {!typing &&
                    author?.type === "visitor" &&
                    ` - ${messageStatusText(status)}`}{" "}
                {status.error && (
                    <>
                        {"- "}
                        <div className="ek-chat__message--details--retry">
                            <span onClick={handleRetrySendMessage}>
                                <FormattedMessage
                                    id="WEBMESSENGER.MESSAGE_DETAILS.RETRY"
                                    defaultMessage="Renvoyer"
                                />
                            </span>
                            <RetryIcon />
                        </div>
                    </>
                )}
            </div>
        )
    );
}

MessageStatus.propTypes = {
    _id: PropTypes.string.isRequired,
    type: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    /**
     * Author of the message
     */
    author: PropTypes.shape({
        _id: PropTypes.string,
        type: PropTypes.oneOf(["operator", "supervisor", "visitor", "agent"]),
        name: PropTypes.string,
    }),
    /**
     * Status of the message
     */
    status: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
        success: PropTypes.bool,
        sentAt: PropTypes.string,
        receivedAt: PropTypes.string,
        readAt: PropTypes.string,
    }),
    /**
     * Typing Mode
     */
    typing: PropTypes.bool,
    /**
     * Show / Hide the message
     */
    show: PropTypes.bool,
    /**
     * Reset the isHover state
     */
    onResetIsHover: PropTypes.func.isRequired,
};

MessageStatus.defaultProps = {
    type: "",
    content: "",
    author: {},
    status: {},
    typing: false,
    show: false,
};

export default MessageStatus;
